import api from '@/services/api';
import type {
  AssociateTemplateRequest,
  PlatformRole,
  ProvisioningProfile,
  ProvisioningProfileRole,
  ProvisioningProfileTemplate,
  ProvisionScope,
  TemplateType,
} from '@/services/provisioningProfileService';

/**
 * PE portfolio API — nomenclature (product language ↔ `tenants.tenant_type`):
 *
 * - **Individual** → `INDIVIDUAL`
 * - **Consultant** → `CONSULTANT`
 * - **Tenant** → `TENANT` (standard company / org tenant)
 * - **PE (portfolio firm)** → `PORTFOLIO`
 *
 * **Portfolio company** always means a **Tenant**-type tenant (`TENANT`) that is linked as a **child**
 * portfolio company of a **PE** (`PORTFOLIO`) firm. Such tenants may **request** that link; PE approves.
 * It does **not** mean the PE firm itself.
 *
 * (The DB may define additional `tenant_type` values; the four above are the usual product categories.)
 */

const BASE = '/pe-portfolio';
/** Tenant segment profiles (TENANT_ADMIN + TENANT_USER) — same as Admin → Provisioning profiles → Tenant. */
const PE_PORTFOLIO_PROFILES = `${BASE}/provisioning/profiles`;

export interface PePortfolioMembershipDto {
  id: string;
  childTenantId: string;
  parentPeTenantId: string;
  status: string;
  peAdminEmailSnapshot: string | null;
  requestedAt: string;
  decidedAt: string | null;
  rejectionReason: string | null;
  hardDeleteOn: string | null;
  version: string;
  updatedAt: string;
  /** Populated on incoming-requests list for PE admins */
  childTenantName?: string | null;
  childTenantDomain?: string | null;
  childHeadquartersLocation?: string | null;
  requesterDisplayName?: string | null;
  requesterEmail?: string | null;
}

/** Optional subtree roots per hierarchy type for delegated data visibility (design §18.4). */
export type PeScopeDataRootsDto = Record<string, { rootNodeId: string }> | null;

export interface PeHierarchyScopeNodeDto {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
}

export type PeHierarchyScopeNodesBundle = {
  BUSINESS: PeHierarchyScopeNodeDto[];
  FUNCTIONAL: PeHierarchyScopeNodeDto[];
  GEOGRAPHIC: PeHierarchyScopeNodeDto[];
};

export interface PePortfolioUserScopeDto {
  id: string;
  parent_pe_tenant_id: string;
  pe_user_id: string;
  child_tenant_id: string;
  assigned_by_user_id: string;
  assigned_at: string;
  revoked_at: string | null;
  /** null / omitted = full company for each hierarchy */
  pe_scope_data_roots?: PeScopeDataRootsDto;
  /** Set on GET list; human-readable labels */
  pe_user_display?: string;
  child_tenant_name?: string;
}

export type PeAdminEmailPreview =
  | { status: 'invalid' }
  | { status: 'none' }
  | { status: 'ambiguous' }
  | { status: 'match'; peFirmName: string };

export interface PePortfolioWorkspaceDto {
  context: 'portfolio' | 'child';
  viewPortfolio: boolean;
  manageInbound: boolean;
  lifecycle: boolean;
  manageScopes: boolean;
  provisionToChild: boolean;
}

export interface PePortfolioCompanySummaryDto {
  childTenantId: string;
  tenantName: string;
  openIssuesCount: number;
  openActionsCount: number;
}

/** Mirrors `DashboardMetrics` for one or more portfolio company tenants. */
export interface PePortfolioDashboardMetricsDto {
  activeRoadmaps: number;
  activeTransformations: number;
  activeRisks: number;
  inchstonesCompleted: number;
  inchstonesPlanned: number;
}

export interface PePortfolioDashboardKeyRisk {
  id: string;
  name: string;
  description?: string | null;
  severity: string;
  probability: string;
  status: string;
  risk_score?: number | null;
  current_owner_name?: string | null;
  portfolio_company_name?: string | null;
  portfolio_tenant_id?: string | null;
  due_date?: string | null;
}

export interface PePortfolioDashboardKeyIssue {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  urgency: string;
  impact: string;
  created_at?: string | null;
  portfolio_company_name?: string | null;
  portfolio_tenant_id?: string | null;
}

export interface PePortfolioDashboardKeyAction {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  priority: string;
  due_date?: string | null;
  created_at?: string | null;
  created_by_user_name?: string;
  portfolio_company_name?: string | null;
  portfolio_tenant_id?: string | null;
}

export interface PePortfolioDashboardBundleDto {
  metrics: PePortfolioDashboardMetricsDto;
  keyRisks: PePortfolioDashboardKeyRisk[];
  keyIssues: PePortfolioDashboardKeyIssue[];
  keyActions: PePortfolioDashboardKeyAction[];
}

export interface PePortfolioScopeOptionsDto {
  companies: Array<{ childTenantId: string; tenantName: string }>;
  peUsers: Array<{ id: string; email: string; firstName: string; lastName: string }>;
}

/** Portfolio companies + users in those companies (for PE provisioning targets). */
export interface PePortfolioProvisioningTargetsDto {
  companies: Array<{ childTenantId: string; tenantName: string }>;
  users: Array<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    tenantId: string;
    tenantName: string;
  }>;
}

export interface PeProvisionTemplateRow {
  id: string;
  name: string;
  description: string | null;
  template_type: string;
  marketplace_visibility: string;
  is_universal: boolean;
}

export interface PeProvisionIndividualResult {
  success: boolean;
  message: string;
  results: Array<{
    templateId: string;
    templateName: string;
    templateType: string;
    status: string;
    errorMessage?: string;
  }>;
  summary: { total: number; successful: number; failed: number; skipped: number };
}

export interface ProvisioningProfileOptionDto {
  profileCode: string;
  profileName: string;
  profileDescription: string | null;
}

export interface TenantProvisioningPreviewDto {
  profileCode: string;
  profileName: string | null;
  profileDescription: string | null;
  templates: Array<{
    templateType: string;
    templateId: string;
    provisionOrder: number;
    alreadyProvisioned: boolean;
    wouldSkip: boolean;
    overwriteRiskIfRuns: boolean;
  }>;
  roles: Array<{
    roleId: string;
    roleName: string | null;
    displayName: string | null;
    tenantRoleExists: boolean;
    wouldSkip: boolean;
    overwriteRiskIfRuns: boolean;
  }>;
  willRunFallbackGlobalHierarchies: boolean;
  fallbackHierarchiesNote: string | null;
  hasAnyOverwriteRisk: boolean;
}

function errMessage(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as {
      response?: {
        data?: { message?: string; code?: string; error?: { message?: string } };
      };
    }).response;
    const m = r?.data?.message ?? r?.data?.error?.message;
    if (typeof m === 'string') return m;
    if (typeof r?.data?.code === 'string') return r.data.code;
  }
  return e instanceof Error ? e.message : 'Request failed';
}

export const pePortfolioService = {
  async getWorkspace(): Promise<PePortfolioWorkspaceDto> {
    const { data } = await api.get<{ success: boolean; data: PePortfolioWorkspaceDto }>(`${BASE}/workspace`);
    return data.data;
  },

  /** Audit-only companion to the delegated-context HTTP header (§18.7). */
  async postDelegatedContext(
    childTenantId: string | null,
    correlationId?: string
  ): Promise<{ childTenantId: string | null; correlationId: string }> {
    const { data } = await api.post<{
      success: boolean;
      data: { childTenantId: string | null; correlationId: string };
    }>(`${BASE}/delegated-context`, { childTenantId, correlationId });
    return data.data;
  },

  async getCurrentMembership(): Promise<PePortfolioMembershipDto | null> {
    const { data } = await api.get<{ success: boolean; data: PePortfolioMembershipDto | null }>(
      `${BASE}/membership/current`
    );
    return data.data ?? null;
  },

  async previewPeAdminEmail(email: string): Promise<PeAdminEmailPreview> {
    const { data } = await api.get<{ success: boolean; data: PeAdminEmailPreview }>(`${BASE}/link-request/pe-admin-preview`, {
      params: { email },
    });
    return data.data;
  },

  async requestLink(peAdminEmail: string): Promise<PePortfolioMembershipDto> {
    const { data } = await api.post<{ success: boolean; data: PePortfolioMembershipDto }>(`${BASE}/link-request`, {
      peAdminEmail,
    });
    return data.data;
  },

  async withdrawRequest(): Promise<PePortfolioMembershipDto> {
    const { data } = await api.post<{ success: boolean; data: PePortfolioMembershipDto }>(
      `${BASE}/link-request/withdraw`
    );
    return data.data;
  },

  async reverseScheduledDeletion(): Promise<PePortfolioMembershipDto> {
    const { data } = await api.post<{ success: boolean; data: PePortfolioMembershipDto }>(
      `${BASE}/deletion/reverse`
    );
    return data.data;
  },

  async listIncoming(): Promise<PePortfolioMembershipDto[]> {
    const { data } = await api.get<{ success: boolean; data: PePortfolioMembershipDto[] }>(
      `${BASE}/incoming-requests`
    );
    return data.data ?? [];
  },

  async approve(membershipId: string, version?: string): Promise<PePortfolioMembershipDto> {
    const { data } = await api.post<{ success: boolean; data: PePortfolioMembershipDto }>(
      `${BASE}/incoming-requests/${membershipId}/approve`,
      version != null ? { version } : {}
    );
    return data.data;
  },

  async reject(membershipId: string, reason?: string, version?: string): Promise<PePortfolioMembershipDto> {
    const { data } = await api.post<{ success: boolean; data: PePortfolioMembershipDto }>(
      `${BASE}/incoming-requests/${membershipId}/reject`,
      { reason, version }
    );
    return data.data;
  },

  async listCompanies(): Promise<PePortfolioMembershipDto[]> {
    const { data } = await api.get<{ success: boolean; data: PePortfolioMembershipDto[] }>(`${BASE}/companies`);
    return data.data ?? [];
  },

  async listScheduledDeletions(): Promise<PePortfolioMembershipDto[]> {
    const { data } = await api.get<{ success: boolean; data: PePortfolioMembershipDto[] }>(
      `${BASE}/companies/scheduled-deletions`
    );
    return data.data ?? [];
  },

  async getConsolidatedSummary(): Promise<{ companies: PePortfolioCompanySummaryDto[] }> {
    const { data } = await api.get<{ success: boolean; data: { companies: PePortfolioCompanySummaryDto[] } }>(
      `${BASE}/consolidated-summary`
    );
    return data.data ?? { companies: [] };
  },

  /** Dashboard metrics + key widgets for visible portfolio companies (aggregated or one child tenant). */
  async getPortfolioDashboard(
    childTenantId?: string | null,
    widgetLimit: number = 5
  ): Promise<PePortfolioDashboardBundleDto> {
    const params: Record<string, string> = { widgetLimit: String(widgetLimit) };
    if (childTenantId) params.childTenantId = childTenantId;
    const { data } = await api.get<{ success: boolean; data?: PePortfolioDashboardBundleDto }>(`${BASE}/dashboard`, {
      params,
    });
    const d = data.data;
    const empty: PePortfolioDashboardBundleDto = {
      metrics: {
        activeRoadmaps: 0,
        activeTransformations: 0,
        activeRisks: 0,
        inchstonesCompleted: 0,
        inchstonesPlanned: 0,
      },
      keyRisks: [],
      keyIssues: [],
      keyActions: [],
    };
    if (!d) return empty;
    return {
      metrics: d.metrics ?? empty.metrics,
      keyRisks: Array.isArray(d.keyRisks) ? d.keyRisks : [],
      keyIssues: Array.isArray(d.keyIssues) ? d.keyIssues : [],
      keyActions: Array.isArray(d.keyActions) ? d.keyActions : [],
    };
  },

  async listProvisioningProfiles(): Promise<ProvisioningProfileOptionDto[]> {
    const { data } = await api.get<{ success: boolean; data: ProvisioningProfileOptionDto[] }>(
      `${BASE}/provisioning-profiles`
    );
    return data.data ?? [];
  },

  async getPePortfolioProvisioningProfile(
    profileCode: 'TENANT_ADMIN' | 'TENANT_USER',
    options?: { tenantAdminView?: 'tenant' | 'tenantAdmin' }
  ): Promise<{
    profile: ProvisioningProfile;
    provisioningProfileSource: 'platform_default' | 'pe_firm_copy';
  }> {
    const params: Record<string, string> = {};
    if (profileCode === 'TENANT_ADMIN') {
      params.view = options?.tenantAdminView === 'tenantAdmin' ? 'tenantAdmin' : 'tenant';
    }
    const { data } = await api.get<{
      success: boolean;
      profile: ProvisioningProfile;
      provisioningProfileSource?: 'platform_default' | 'pe_firm_copy';
    }>(`${PE_PORTFOLIO_PROFILES}/${encodeURIComponent(profileCode)}`, { params });
    return {
      profile: data.profile,
      provisioningProfileSource: data.provisioningProfileSource ?? 'platform_default',
    };
  },

  async deletePePortfolioProvisioningFork(profileCode: 'TENANT_ADMIN' | 'TENANT_USER'): Promise<void> {
    await api.delete(`${PE_PORTFOLIO_PROFILES}/${encodeURIComponent(profileCode)}/fork`);
  },

  async updatePePortfolioProvisioningProfile(
    profileCode: 'TENANT_ADMIN' | 'TENANT_USER',
    updates: { profile_name?: string; profile_description?: string }
  ): Promise<ProvisioningProfile> {
    const { data } = await api.put<{ success: boolean; profile: ProvisioningProfile }>(
      `${PE_PORTFOLIO_PROFILES}/${encodeURIComponent(profileCode)}`,
      updates
    );
    return data.profile;
  },

  async associatePePortfolioTemplate(
    profileCode: 'TENANT_ADMIN' | 'TENANT_USER',
    request: Omit<AssociateTemplateRequest, 'profileId'>
  ): Promise<ProvisioningProfileTemplate> {
    const { data } = await api.post<{ success: boolean; template: ProvisioningProfileTemplate }>(
      `${PE_PORTFOLIO_PROFILES}/${encodeURIComponent(profileCode)}/templates`,
      {
        templateType: request.templateType,
        templateId: request.templateId,
        templateName: request.templateName,
        isRequired: request.isRequired,
        provisionOrder: request.provisionOrder,
        provisionScope: request.provisionScope,
      }
    );
    return data.template;
  },

  async removePePortfolioTemplate(
    profileCode: 'TENANT_ADMIN' | 'TENANT_USER',
    templateType: TemplateType,
    templateId: string
  ): Promise<void> {
    await api.delete(`${PE_PORTFOLIO_PROFILES}/${encodeURIComponent(profileCode)}/templates`, {
      data: { templateType, templateId },
    });
  },

  async associatePePortfolioRole(
    profileCode: 'TENANT_ADMIN' | 'TENANT_USER',
    request: { roleId: string; provisionOrder: number; provisionScope?: ProvisionScope }
  ): Promise<ProvisioningProfileRole> {
    const { data } = await api.post<{ success: boolean; role: ProvisioningProfileRole }>(
      `${PE_PORTFOLIO_PROFILES}/${encodeURIComponent(profileCode)}/roles`,
      {
        roleId: request.roleId,
        provisionOrder: request.provisionOrder,
        ...(request.provisionScope !== undefined ? { provisionScope: request.provisionScope } : {}),
      }
    );
    return data.role;
  },

  async removePePortfolioRole(profileCode: 'TENANT_ADMIN' | 'TENANT_USER', roleId: string): Promise<void> {
    await api.delete(`${PE_PORTFOLIO_PROFILES}/${encodeURIComponent(profileCode)}/roles`, {
      data: { roleId },
    });
  },

  async getPePortfolioAvailableRoles(context: 'tenant' | 'userProfile'): Promise<PlatformRole[]> {
    const { data } = await api.get<{ success: boolean; roles: PlatformRole[] }>(
      `${BASE}/provisioning/available-roles`,
      { params: { context } }
    );
    return data.roles ?? [];
  },

  async getProvisioningTargets(): Promise<PePortfolioProvisioningTargetsDto> {
    const { data } = await api.get<{ success: boolean; data: PePortfolioProvisioningTargetsDto }>(
      `${BASE}/provisioning/targets`
    );
    return data.data ?? { companies: [], users: [] };
  },

  async getProvisionAvailableTemplates(options: {
    templateTypes: string[];
    provisionScope: 'USER' | 'TENANT';
  }): Promise<PeProvisionTemplateRow[]> {
    const { data } = await api.get<{ success: boolean; templates: PeProvisionTemplateRow[] }>(
      `${BASE}/provisioning/available-templates`,
      {
        params: {
          templateTypes: options.templateTypes.join(','),
          provisionScope: options.provisionScope,
        },
      }
    );
    return data.templates ?? [];
  },

  async provisionIndividualAssets(body: {
    templateIds: string[];
    targetType: 'USER' | 'TENANT';
    targetUserId?: string;
    targetTenantId?: string;
  }): Promise<PeProvisionIndividualResult> {
    const { data } = await api.post<PeProvisionIndividualResult>(`${BASE}/provisioning/provision-assets`, body);
    return data;
  },

  async getProvisionPreview(childTenantId: string, profileCode: string): Promise<TenantProvisioningPreviewDto> {
    const { data } = await api.get<{ success: boolean; data: TenantProvisioningPreviewDto }>(
      `${BASE}/companies/${encodeURIComponent(childTenantId)}/provision-preview`,
      { params: { profileCode } }
    );
    return data.data;
  },

  async provisionToChild(
    childTenantId: string,
    options?: { profileCode?: string; confirmOverwrite?: boolean }
  ): Promise<{ provisioned: number; total: number }> {
    const { data } = await api.post<{
      success: boolean;
      data: { provisioned: number; total: number; profileCode: string };
    }>(`${BASE}/companies/${encodeURIComponent(childTenantId)}/provision`, {
      profileCode: options?.profileCode ?? 'TENANT_ADMIN',
      confirmOverwrite: options?.confirmOverwrite === true,
    });
    return { provisioned: data.data.provisioned, total: data.data.total };
  },

  async getConnectorsStatus(): Promise<{ enabled: boolean; message: string; supportedProviders: string[] }> {
    const { data } = await api.get<{
      success: boolean;
      data: { enabled: boolean; message: string; supportedProviders: string[] };
    }>(`${BASE}/connectors/status`);
    return data.data;
  },

  async listSyntheticDefinitions(): Promise<
    Array<{
      id: string;
      name: string;
      code: string;
      operation: string;
      is_active: boolean;
    }>
  > {
    const { data } = await api.get<{ success: boolean; data: unknown[] }>(`${BASE}/synthetic-metrics/definitions`);
    return (data.data ?? []) as Array<{
      id: string;
      name: string;
      code: string;
      operation: string;
      is_active: boolean;
    }>;
  },

  async removeCompany(childTenantId: string): Promise<void> {
    await api.post(`${BASE}/companies/${childTenantId}/remove`);
  },

  async scheduleDelete(childTenantId: string): Promise<PePortfolioMembershipDto> {
    const { data } = await api.post<{ success: boolean; data: PePortfolioMembershipDto }>(
      `${BASE}/companies/${childTenantId}/schedule-delete`
    );
    return data.data;
  },

  async revertDelete(childTenantId: string): Promise<PePortfolioMembershipDto> {
    const { data } = await api.post<{ success: boolean; data: PePortfolioMembershipDto }>(
      `${BASE}/companies/${childTenantId}/revert-delete`
    );
    return data.data;
  },

  async listScopes(): Promise<PePortfolioUserScopeDto[]> {
    const { data } = await api.get<{ success: boolean; data: PePortfolioUserScopeDto[] }>(`${BASE}/user-scopes`);
    return data.data ?? [];
  },

  async getScopeOptions(): Promise<PePortfolioScopeOptionsDto> {
    const { data } = await api.get<{ success: boolean; data: PePortfolioScopeOptionsDto }>(
      `${BASE}/user-scopes/options`
    );
    return data.data;
  },

  async assignScope(
    peUserId: string,
    childTenantId: string,
    peScopeDataRoots?: Record<string, { rootNodeId: string }> | null
  ): Promise<PePortfolioUserScopeDto> {
    const { data } = await api.post<{ success: boolean; data: PePortfolioUserScopeDto }>(`${BASE}/user-scopes`, {
      peUserId,
      childTenantId,
      ...(peScopeDataRoots !== undefined ? { peScopeDataRoots } : {}),
    });
    return data.data;
  },

  async getChildHierarchyScopeNodes(childTenantId: string): Promise<PeHierarchyScopeNodesBundle> {
    const { data } = await api.get<{ success: boolean; data: PeHierarchyScopeNodesBundle }>(
      `${BASE}/companies/${encodeURIComponent(childTenantId)}/hierarchy-scope-nodes`
    );
    return data.data;
  },

  async updateScopeDataRoots(
    scopeId: string,
    peScopeDataRoots: Record<string, { rootNodeId: string }> | null
  ): Promise<PePortfolioUserScopeDto> {
    const { data } = await api.put<{ success: boolean; data: PePortfolioUserScopeDto }>(
      `${BASE}/user-scopes/${encodeURIComponent(scopeId)}/data-roots`,
      { peScopeDataRoots }
    );
    return data.data;
  },

  async revokeScope(scopeId: string): Promise<void> {
    await api.delete(`${BASE}/user-scopes/${scopeId}`);
  },

  errMessage,
};
